import { Sun, Moon, SunMoon, Monitor, Clock, Check } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const OPTIONS = [
    { value: "auto", label: "Otomatis", hint: "Gelap 18.00, terang 06.00 WIB" },
    { value: "system", label: "Ikuti Sistem", hint: "Mengikuti pengaturan perangkat" },
    { value: "light", label: "Selalu Terang" },
    { value: "dark", label: "Selalu Gelap" },
];

// Varian "auth" (halaman login & verifikasi, AuthShell.jsx): urutan, label & ikon
// sendiri, tapi mode/penyimpanan yg sama persis dgn menu di dalam aplikasi.
const AUTH_OPTIONS = [
    { value: "light", label: "Terang", Icon: Sun },
    { value: "dark", label: "Gelap", Icon: Moon },
    { value: "system", label: "Ikuti sistem", Icon: Monitor },
    { value: "auto", label: "Otomatis", hint: "Gelap 18.00, terang 06.00 WIB", Icon: Clock },
];
const AUTH_ICONS = { light: Sun, dark: Moon, system: Monitor, auto: Clock };

// Toggle tema (dekat profil pengguna di sidebar). "Otomatis" mengikuti jam
// WIB; override manual disimpan per perangkat (localStorage), lihat ThemeContext.
// `large`: dipakai di rail sidebar collapsed (AppLayout.jsx) supaya ukurannya
// konsisten dgn ikon menu navigasi (20px) di mode itu -- default (dipakai di
// baris "Login sebagai" versi expanded) tetap 16px/28px spt semula.
// `variant="auth"`: tombol berbingkai 40px utk pojok kanan atas halaman login;
// tanpa prop ini perilaku & tampilan di dalam aplikasi tidak berubah.
export default function ThemeToggle({ large = false, variant = "sidebar" }) {
    const theme = useTheme();
    if (!theme) return null;
    const { mode, effectiveTheme, setMode } = theme;
    const isAuth = variant === "auth";
    const Icon = isAuth ? (AUTH_ICONS[mode] || Clock)
        : mode === "auto" ? SunMoon
        : mode === "system" ? Monitor
        : effectiveTheme === "dark" ? Moon : Sun;
    const options = isAuth ? AUTH_OPTIONS : OPTIONS;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={isAuth
                        ? "h-10 w-10 rounded-xl border border-border bg-card text-slate-700 shadow-sm hover:bg-muted hover:text-slate-900 focus-visible:ring-2"
                        : `${large ? "h-9 w-9" : "h-7 w-7"} text-white hover:bg-black/10 hover:text-white shrink-0`}
                    aria-label={isAuth ? "Pilih tema" : "Ubah tema tampilan"}
                    data-testid={isAuth ? "auth-theme-button" : "theme-toggle-button"}
                >
                    {/* Button shadcn memaksa svg ke 16px ([&_svg]:size-4, spesifisitas menang
                        lawan className biasa) -- prefix `!` (important) wajib dipakai supaya
                        versi large benar2 render 20px. */}
                    <Icon className={isAuth ? "!w-[18px] !h-[18px]" : large ? "!w-5 !h-5" : "w-4 h-4"} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                collisionPadding={isAuth ? 16 : undefined}
                className={isAuth ? "min-w-[13rem] rounded-xl" : undefined}
                data-testid={isAuth ? "auth-theme-menu" : "theme-toggle-menu"}
            >
                <DropdownMenuLabel className="text-xs">{isAuth ? "Tema" : "Tema Tampilan"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {options.map((opt) => (
                    <DropdownMenuItem
                        key={opt.value}
                        onClick={() => setMode(opt.value)}
                        data-testid={isAuth ? `auth-theme-${opt.value}` : `theme-option-${opt.value}`}
                        className={`flex items-center justify-between gap-3${isAuth ? " min-h-10" : ""}`}
                    >
                        <span className={isAuth ? "flex items-center gap-2.5" : undefined}>
                            {opt.Icon && <opt.Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
                            <span>
                                {opt.label}
                                {/* Varian auth: saat item disorot (latar accent), hint ikut warna
                                    teks item supaya kontras tetap >= 4.5:1. */}
                                {opt.hint && (
                                    <span className={`block text-[10px] text-muted-foreground${isAuth ? " [[role=menuitem]:focus_&]:text-current" : ""}`}>
                                        {opt.hint}
                                    </span>
                                )}
                            </span>
                        </span>
                        {mode === opt.value && <Check className="w-3.5 h-3.5 shrink-0" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
